import { useId, useState } from 'react';
import type { Package, PackageDraft } from '@mypoz/shared';
import {
  DECLARED_VALUE_CURRENCY,
  EMPTY_PACKAGE_DRAFT,
  gramsToKg,
  isPackageDraftComplete,
  isPackageLocked,
  isTrackingNumberOnAccount,
  packageDraftChanged,
  packageDraftFrom,
  sanitiseMessageContent,
} from '@mypoz/shared';
import { findEntry, useTranslation } from '@mypoz/i18n';
import { cn, Icon, Select, Sheet } from '@mypoz/ui';

import { PackageStatusChip } from '@/features/packages/components/PackageStatusChip.js';
import { usePackages } from '@/features/packages/context.js';
import { PackagesRequestError } from '@/features/packages/api.js';
import {
  ADD_PACKAGE_ERROR,
  ADD_PACKAGE_SHOWS_VENDOR_TEXT,
  EDIT_PACKAGE_ERROR,
  isUnconfirmedFailure,
} from '@/features/packages/dictionary.js';
import { useWarehouse, useWarehouses } from '@/features/warehouses/index.js';
import { useToast } from '@/features/toast/index.js';
import { useAutoTour } from '@/features/tour/index.js';
import { actionLinkClass, buttonClass, inputClass } from '@/lib/styles.js';
import { ServiceCard } from '@/components/ServiceCard.js';
import { FieldLabel } from '@/components/ui.js';
import { useFormat } from '@/lib/format.js';
import { GOODS_TYPE_HELP_URL } from '@/lib/legacy-api.js';

export type PackageFormMode = 'add' | 'edit' | 'view';

export interface PackageFormSheetProps {
  /** Absent in `add` mode; the package being looked at or edited otherwise. */
  package?: Package;
  mode: PackageFormMode;
  onClose: () => void;
  /**
   * MYP-38: opens the package's message thread instead. Absent in `add` mode, the one case with no
   * package yet to have a thread — see the footer's own conditional rendering below.
   */
  onOpenMessages?: () => void;
  /**
   * View mode only: switches this same sheet into Edit. Absent when there is nothing to switch to
   * — a locked package, or Add/Edit mode, which are already editing.
   */
  onEdit?: () => void;
}

/** A labelled, read-only line — View mode's building block, and Edit mode's warehouse row. */
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-line py-3 first:pt-0 last:border-0">
      <p className="text-sm text-muted">{label}</p>
      <p className="mt-0.5 text-sm text-ink">{value}</p>
    </div>
  );
}

/**
 * The read-only facts the warehouse itself supplies at intake (MYP-37) — never editable here, the
 * same way a package's `status` isn't: a customer can correct what they told the warehouse, not
 * what the warehouse measured. Shown in both Edit and View, since it's equally relevant to either;
 * absent entirely in Add mode, since nothing exists yet for a warehouse to have measured.
 */
function MeasuredByWarehouse({ pkg }: { pkg: Package }) {
  const { t } = useTranslation();
  const fmt = useFormat();

  if (pkg.weightGrams === undefined) {
    return (
      <p className="rounded-md bg-surface-alt px-3 py-2.5 text-sm text-muted">
        {t('packages.form.notYetMeasured')}
      </p>
    );
  }

  return (
    <div className="rounded-md border-l-2 border-brand bg-surface-alt py-2.5 pr-3 pl-3">
      <p className="text-xs font-semibold tracking-[0.08em] text-muted uppercase">
        {t('packages.form.measuredByWarehouse')}
      </p>
      <div className="mt-1.5 grid gap-1 text-sm">
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-muted">{t('packages.form.weight')}</span>
          <span className="text-ink tabular-nums">
            {t('orders.newOrder.weightKg', { value: fmt.number(gramsToKg(pkg.weightGrams), 1) })}
          </span>
        </div>
        {pkg.dimensionsCm ? (
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-muted">{t('packages.form.dimensions')}</span>
            <span className="text-ink tabular-nums">
              {t('packages.form.dimensionsCm', {
                length: fmt.number(pkg.dimensionsCm.length),
                width: fmt.number(pkg.dimensionsCm.width),
                height: fmt.number(pkg.dimensionsCm.height),
              })}
            </span>
          </div>
        ) : null}
        {pkg.shelfLocation ? (
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-muted">{t('packages.form.shelf')}</span>
            <span className="text-ink tabular-nums">{pkg.shelfLocation}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Why the last Add did not go through (MYP-98): a duplicate caught before any request was sent, or
 * the vendor's own refusal code with whatever text came with it.
 */
type AddError =
  | { readonly kind: 'duplicate' }
  | { readonly kind: 'vendor'; readonly code: string; readonly detail: string };

/**
 * The inline refusal under the form. A documented code gets its own translated line; -2/-3/-4
 * share one (the document never says which is which) with the vendor's own wording beneath; a
 * lost answer says plainly that the outcome is unknown; anything else shows its code, never a
 * guessed meaning.
 */
function AddErrorMessage({
  id,
  error,
  editing = false,
}: {
  id: string;
  error: AddError;
  /** An edit (MYP-123) goes through the same endpoint, but says "saved", not "added". */
  editing?: boolean;
}) {
  const { t } = useTranslation();

  let message: string;
  let detail: string | undefined;
  if (error.kind === 'duplicate') {
    message = t('packages.form.addError.duplicate');
  } else {
    const entry = findEntry(editing ? EDIT_PACKAGE_ERROR : ADD_PACKAGE_ERROR, error.code);
    if (entry) {
      message = t(entry.labelKey);
      if (ADD_PACKAGE_SHOWS_VENDOR_TEXT.includes(error.code)) detail = error.detail || undefined;
    } else if (isUnconfirmedFailure(error.code)) {
      message = t('packages.form.addError.unconfirmed');
    } else {
      message = editing
        ? t('packages.form.editError.unknown', { code: error.code })
        : t('packages.form.addError.unknown', { code: error.code });
      detail = error.detail || undefined;
    }
  }

  return (
    <div id={id} role="alert" className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2">
      <p className="text-sm text-danger">{message}</p>
      {detail ? (
        <p className="mt-1 text-xs text-muted">
          {t('packages.form.addError.vendorSaid', { detail })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Add/Edit/View for one package (MYP-33; MYP-34's services section; MYP-37 grows it into the
 * fuller "detail view" the mentor mockup shows) — opened from the list's per-row Edit/View link,
 * and from its own "Add package" button for a new one. `mode` is decided by the caller from
 * `isPackageLocked`, not here: this component trusts what it's told rather than re-deriving the
 * lock rule, since MYP-37 owns changing that rule later.
 *
 * Warehouse is only ever *chosen* in Add mode — see `PackageDraft`'s doc comment for why Edit
 * shows it read-only instead of as a second picker. Which services can be ticked follows whichever
 * warehouse is current either way, since `servicesFor` (live `packages_services`) says what's offered;
 * changing the warehouse in Add mode drops any ticked service the new one doesn't have, per
 * MYP-34's own description.
 *
 * MYP-37 adds the fields the mentor mockup shows beyond MYP-33's deliberately minimal scope —
 * courier company (no longer asked for since MYP-145), a Chinese item name, declared value,
 * remarks, and the warehouse-measured weight/dimensions/shelf block — plus a Messages link in the
 * footer. That link is a stand-in for
 * MYP-38 (package messages), which isn't built yet: it shows the same "not connected yet"
 * acknowledgement Save already does, rather than opening a thread that doesn't exist.
 *
 * Save in **Add** mode is live since MYP-98: it calls `add_packages` and re-reads the list. Three
 * fields that endpoint takes are not on this form and are deliberately not sent — `transtype`,
 * `revname`, `pkgnum` — see `toAddPackageParams` for why each is left to the vendor's default. A
 * tracking number already on the account is caught here, before any request; everything else the
 * vendor refuses is shown inline with the sheet kept open and the draft intact.
 *
 * Save in **Edit** mode is live since MYP-123. There is still no update endpoint: the vendor's
 * answer to question 7 is that `add_packages` updates a package whose tracking number already
 * exists. So Edit resubmits the package (`toUpdatePackageParams`) with its tracking number and
 * warehouse fixed, its hidden parcel count and transport sent back unchanged, and no services —
 * `package_list` never says which ones it has. Save stays off until something actually changed.
 *
 * The measured weight/dimensions/shelf block reads live data now, with one gap: `package_list`
 * returns a weight and a shelf but no length/width/height — only a single `vol` figure — so the
 * dimensions line has nothing to render and stays hidden, as it already did for an unmeasured
 * package. Applied services are the same story: `pkgservices` goes in via `add_packages` and never
 * comes back on the row, so the picker can offer the list but cannot show what was chosen before.
 *
 * Services are the vendor's own per-warehouse lists since MYP-100, shown as cards with the
 * service's picture and its price (`fee`, which the vendor confirmed is what customers see —
 * MYP-80, MYP-116). There is still no total: a package's services are billed with the order.
 */
export function PackageFormSheet({
  package: pkg,
  mode,
  onClose,
  onOpenMessages,
  onEdit,
}: PackageFormSheetProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const toast = useToast();
  const {
    goodsTypes,
    servicesFor,
    servicesStatus,
    reloadServices,
    packages,
    addPackage,
    updatePackage,
  } = usePackages();
  const itemNameId = useId();
  const itemNameCnId = useId();
  const trackingNumberId = useId();
  const quantityId = useId();
  const declaredValueId = useId();
  const remarkId = useId();
  const warehouseId = useId();
  const addErrorId = useId();
  const formId = useId();

  const [draft, setDraft] = useState<PackageDraft>(() =>
    pkg ? packageDraftFrom(pkg) : EMPTY_PACKAGE_DRAFT,
  );

  // MYP-98: an in-flight add, and why the last one failed. Any edit to the draft clears the error —
  // it described the draft as it was, not as it is now.
  const [submitting, setSubmitting] = useState(false);
  const [addError, setAddError] = useState<AddError | undefined>(undefined);

  const patch = (next: Partial<PackageDraft>) => {
    setAddError(undefined);
    setDraft((current) => ({ ...current, ...next }));
  };

  // The one update that can't just merge a partial: picking a new warehouse also has to drop any
  // ticked service the new warehouse doesn't offer, per MYP-34's own description.
  function selectWarehouse(nextWarehouseId: string) {
    const offered = new Set(servicesFor(nextWarehouseId).map((service) => service.id));
    setDraft((current) => ({
      ...current,
      warehouseId: nextWarehouseId,
      serviceIds: current.serviceIds.filter((id) => offered.has(id)),
    }));
  }

  function toggleService(id: string) {
    setDraft((current) => ({
      ...current,
      serviceIds: current.serviceIds.includes(id)
        ? current.serviceIds.filter((item) => item !== id)
        : [...current.serviceIds, id],
    }));
  }

  const { warehouses } = useWarehouses();
  // MYP-83: the first time a customer opens the add form, a short tour of its fields.
  useAutoTour('add_package', mode === 'add');
  // Goods types (MYP-102) load with the package list, and `?panel=add` can open this sheet before
  // that finishes or after it failed — so that choice is hidden while there is nothing to offer,
  // rather than rendering an empty group. The draft keeps its GENERAL default.
  const warehouse = useWarehouse(pkg?.warehouseId);
  const availableServices = draft.warehouseId ? servicesFor(draft.warehouseId) : [];

  const title =
    mode === 'add'
      ? t('packages.form.addTitle')
      : mode === 'edit'
        ? t('packages.form.editTitle', { tracking: pkg?.trackingNumber ?? '' })
        : t('packages.form.viewTitle', { tracking: pkg?.trackingNumber ?? '' });

  // MYP-123: Save in Edit stays off until something the update would send has changed.
  const editing = mode === 'edit' && pkg !== undefined;
  const changed = editing ? packageDraftChanged(draft, pkg) : true;

  async function handleSubmit() {
    // A double click, or Enter held down, must not send two creates (or two updates).
    if (submitting) return;
    // The same rule the Save button follows (MYP-129): a submit that reaches here some other way —
    // Enter in a field — must not send a package with a required field missing or blank.
    if (!isPackageDraftComplete(draft)) return;

    if (mode === 'edit') {
      // MYP-123: `add_packages` updates a package whose tracking number already exists (vendor's
      // answer to question 7). No duplicate check here — in Edit the number is meant to exist.
      if (!pkg || !packageDraftChanged(draft, pkg)) return;
      setSubmitting(true);
      setAddError(undefined);
      try {
        await updatePackage(draft, pkg);
        toast.show(t('packages.form.saved', { tracking: pkg.trackingNumber }));
        onClose();
      } catch (cause) {
        console.error('[packages] add_packages (update) failed', cause);
        setAddError(
          cause instanceof PackagesRequestError
            ? { kind: 'vendor', code: cause.code, detail: cause.detail }
            : { kind: 'vendor', code: 'UNKNOWN', detail: '' },
        );
        setSubmitting(false);
      }
      return;
    }

    if (mode !== 'add') return;

    // Caught locally, with no request at all: the tracking number is already on this account.
    if (isTrackingNumberOnAccount(packages, draft.trackingNumber)) {
      setAddError({ kind: 'duplicate' });
      return;
    }

    setSubmitting(true);
    setAddError(undefined);
    try {
      await addPackage(draft);
      toast.show(t('packages.form.added', { tracking: draft.trackingNumber.trim() }));
      onClose();
    } catch (cause) {
      console.error('[packages] add_packages failed', cause);
      setAddError(
        cause instanceof PackagesRequestError
          ? { kind: 'vendor', code: cause.code, detail: cause.detail }
          : { kind: 'vendor', code: 'UNKNOWN', detail: '' },
      );
      setSubmitting(false);
    }
  }

  if (mode === 'view' && pkg) {
    const appliedServices = servicesFor(pkg.warehouseId).filter((service) =>
      pkg.serviceCodes.includes(service.id),
    );

    return (
      <Sheet title={title} onClose={onClose} closeLabel={t('packages.form.closeDialog', { title })}>
        <MeasuredByWarehouse pkg={pkg} />

        <InfoRow label={t('packages.form.trackingNumber')} value={pkg.trackingNumber} />
        {pkg.courierName ? (
          <InfoRow label={t('packages.form.courierName')} value={pkg.courierName} />
        ) : null}
        <InfoRow label={t('packages.form.itemName')} value={pkg.itemNameEn} />
        {pkg.itemNameCn ? (
          <InfoRow label={t('packages.form.itemNameCn')} value={pkg.itemNameCn} />
        ) : null}
        <InfoRow label={t('packages.form.quantity')} value={String(pkg.quantity)} />
        {pkg.declaredValueCents !== undefined ? (
          <InfoRow
            label={t('packages.form.declaredValue')}
            // In RMB, as declared (MYP-145) — not the app's default ringgit.
            value={fmt.money(pkg.declaredValueCents, DECLARED_VALUE_CURRENCY)}
          />
        ) : null}
        <InfoRow
          label={t('packages.form.warehouse')}
          value={warehouse ? warehouse.label : pkg.warehouseId}
        />
        <div className="py-3">
          <p className="text-sm text-muted">{t('packages.form.status')}</p>
          <div className="mt-1.5">
            <PackageStatusChip status={pkg.status} label={pkg.statusLabel} />
          </div>
        </div>

        <div className="py-3">
          <p className="text-sm text-muted">{t('packages.form.services.title')}</p>
          {appliedServices.length > 0 ? (
            <div className="mt-1.5 grid gap-1">
              {appliedServices.map((service) => (
                <div key={service.id} className="flex items-baseline justify-between gap-4">
                  <span className="text-sm text-ink">{service.name}</span>
                  <span className="text-sm text-muted">
                    {service.feeCents !== undefined
                      ? fmt.money(service.feeCents, service.currency)
                      : t('packages.form.services.pricedByWarehouse')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-0.5 text-sm text-ink">—</p>
          )}
        </div>

        {pkg.remark ? <InfoRow label={t('packages.form.remark')} value={pkg.remark} /> : null}

        {/* Only shown when the package really is locked. View used to imply locked — it was the
            only way to reach this branch — but every row offers View now, so an editable package
            can be read here too and must not be told it can no longer be edited. */}
        {isPackageLocked(pkg) ? (
          <p className="mt-2 flex items-start gap-2 rounded-md bg-surface-alt px-3 py-2.5 text-sm text-muted">
            <Icon name="lock" size={18} className="mt-0.5 shrink-0" />
            {t('packages.form.lockedNotice')}
          </p>
        ) : null}

        <div className="mt-5 flex items-center justify-between gap-4">
          <button type="button" onClick={onClose} className={buttonClass('secondary')}>
            {t('packages.form.close')}
          </button>
          <div className="flex items-center gap-4">
            {/* So reading an editable package isn't a dead end that has to be closed and reopened
                from the row to change anything. */}
            {onEdit ? (
              <button type="button" onClick={onEdit} className={actionLinkClass()}>
                {t('packages.list.action.edit')}
              </button>
            ) : null}
            <button type="button" onClick={() => onOpenMessages?.()} className={actionLinkClass()}>
              {t('packages.form.messages')}
            </button>
          </div>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet
      title={title}
      onClose={onClose}
      closeLabel={t('packages.form.closeDialog', { title })}
      // MYP-117: Save stays pinned under the form, so a long services list never hides it.
      footer={
        <div className="grid gap-3">
          {addError ? <AddErrorMessage id={addErrorId} error={addError} editing={editing} /> : null}

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                type="submit"
                form={formId}
                data-tour="package-form-save"
                disabled={!isPackageDraftComplete(draft) || !changed || submitting}
                aria-busy={submitting || undefined}
                className={buttonClass(
                  'primary',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                {submitting ? t('packages.form.saving') : t('packages.form.save')}
              </button>
              <button type="button" onClick={onClose} className={actionLinkClass()}>
                {t('packages.form.cancel')}
              </button>
            </div>
            {pkg ? (
              <button
                type="button"
                onClick={() => onOpenMessages?.()}
                className={actionLinkClass()}
              >
                {t('packages.form.messages')}
              </button>
            ) : null}
          </div>
        </div>
      }
    >
      <form
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
        className="grid gap-4"
      >
        {pkg ? <MeasuredByWarehouse pkg={pkg} /> : null}

        {/* MYP-129: says what the red * on the labels means. */}
        <p className="text-sm text-muted">{t('packages.form.requiredNote')}</p>

        {editing ? (
          // MYP-123: the vendor ignores an empty value on update, so a filled-in field can be
          // changed but not emptied — said up front rather than discovered after "saved".
          <p className="text-sm text-muted">{t('packages.form.editHint')}</p>
        ) : null}

        <div data-tour="package-form-tracking">
          {/* Read-only in Edit, so nothing to fill and no *. */}
          <FieldLabel htmlFor={trackingNumberId} required={!editing}>
            {t('packages.form.trackingNumber')}
          </FieldLabel>
          <input
            id={trackingNumberId}
            type="text"
            // MYP-123: the tracking number is what the vendor matches an update on — changing it
            // would add a second package rather than edit this one, so Edit shows it read-only.
            readOnly={editing}
            required={!editing}
            aria-invalid={addError?.kind === 'duplicate' ? true : undefined}
            aria-describedby={
              addError?.kind === 'duplicate'
                ? addErrorId
                : editing
                  ? `${trackingNumberId}-note`
                  : undefined
            }
            value={draft.trackingNumber}
            placeholder={t('packages.form.trackingNumberPlaceholder')}
            onChange={(event) => patch({ trackingNumber: event.target.value })}
            className={inputClass(editing ? 'bg-surface-alt text-muted' : undefined)}
          />
          {editing ? (
            <p id={`${trackingNumberId}-note`} className="mt-1.5 text-xs text-muted">
              {t('packages.form.trackingLocked')}
            </p>
          ) : null}
        </div>

        {/*
          MYP-145: no courier company field — the old website does not ask for one. A package that
          already has one keeps it: an edit sends it back unchanged (`toUpdatePackageParams`).
        */}
        <div data-tour="package-form-warehouse" className="grid gap-4">
          {mode === 'add' ? (
            <div>
              <FieldLabel htmlFor={warehouseId} required>
                {t('packages.filters.warehouse')}
              </FieldLabel>
              <Select
                id={warehouseId}
                required
                value={draft.warehouseId}
                onChange={selectWarehouse}
                options={warehouses.map((item) => ({ value: item.id, label: item.label }))}
                placeholder={t('packages.form.selectWarehouse')}
              />
            </div>
          ) : (
            // Fixed once a package exists — see PackageDraft's doc comment. Shown as a disabled-
            // looking field rather than `InfoRow` so it sits naturally alongside Courier company
            // in this grid; it's still never an editable control here.
            <div>
              <p className="mb-1.5 text-sm text-muted">{t('packages.form.warehouse')}</p>
              <p className={cn(inputClass(), 'flex items-center bg-surface-alt text-muted')}>
                {warehouse ? warehouse.label : draft.warehouseId}
              </p>
            </div>
          )}
        </div>

        {/*
          MYP-145: the old website's warning, word for word in Chinese — the declared name and
          value go to customs, and a wrong one is the customer's to answer for.
        */}
        <p className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
          <Icon name="error" size={18} className="mt-px shrink-0" />
          <span>{t('packages.form.declarationWarning')}</span>
        </p>

        <div data-tour="package-form-item" className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel htmlFor={itemNameId} required>
              {t('packages.form.itemName')}
            </FieldLabel>
            <input
              id={itemNameId}
              type="text"
              required
              value={draft.itemNameEn}
              placeholder={t('packages.form.itemNamePlaceholder')}
              onChange={(event) => patch({ itemNameEn: event.target.value })}
              className={inputClass()}
            />
          </div>

          <div>
            <FieldLabel htmlFor={itemNameCnId}>{t('packages.form.itemNameCn')}</FieldLabel>
            <input
              id={itemNameCnId}
              type="text"
              value={draft.itemNameCn}
              placeholder={t('packages.form.itemNameCnPlaceholder')}
              onChange={(event) => patch({ itemNameCn: event.target.value })}
              className={inputClass()}
            />
          </div>
        </div>

        {goodsTypes.length > 0 ? (
          <div>
            {/*
              MYP-145: how to tell general from sensitive goods, on MyPoz's own site. A new tab, so
              what has been typed here stays; a link beside the label, never one of the choices.
              No negative margin on its 44px tap area: on a phone the row grows instead, so the
              area never reaches over the 普货/敏感 buttons below (review of #52).
            */}
            <div className="mb-1.5 flex items-center gap-1">
              <p className="text-sm text-muted">{t('packages.filters.goodsType')}</p>
              <a
                href={GOODS_TYPE_HELP_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('packages.form.goodsTypeHelp')}
                title={t('packages.form.goodsTypeHelp')}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded text-brand transition-colors hover:text-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-0 lg:min-w-0"
              >
                <Icon name="help" size={18} />
              </a>
            </div>
            <div
              role="group"
              aria-label={t('packages.filters.goodsType')}
              className="inline-flex rounded-md border border-line p-1"
            >
              {goodsTypes.map((entry) => {
                const active = draft.goodsType === entry.code;
                return (
                  <button
                    key={entry.code}
                    type="button"
                    aria-pressed={active}
                    onClick={() => patch({ goodsType: entry.code })}
                    className={cn(
                      'min-h-11 rounded px-4 text-sm font-medium transition-colors lg:min-h-9',
                      active ? 'bg-ink text-white' : 'text-muted hover:text-ink',
                    )}
                  >
                    {entry.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel htmlFor={quantityId} required>
              {t('packages.form.quantity')}
            </FieldLabel>
            <input
              id={quantityId}
              type="number"
              required
              min={1}
              value={draft.quantity}
              onChange={(event) =>
                patch({ quantity: Math.max(1, Number(event.target.value) || 1) })
              }
              className={inputClass()}
            />
          </div>

          <div>
            <FieldLabel htmlFor={declaredValueId} required>
              {t('packages.form.declaredValue')}
            </FieldLabel>
            {/*
              The ¥ in the box as well as in the label (review of #52): this is the one field where
              typing ringgit by habit under-declares to customs. Decorative — the label names RMB.
            */}
            <div className="relative">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted"
              >
                {t('packages.form.declaredValueSymbol')}
              </span>
              <input
                id={declaredValueId}
                type="number"
                required
                min={0.01}
                step="0.01"
                value={draft.declaredValueCents === undefined ? '' : draft.declaredValueCents / 100}
                onChange={(event) => {
                  const raw = event.target.value;
                  patch({
                    declaredValueCents: raw === '' ? undefined : Math.round(Number(raw) * 100),
                  });
                }}
                className={inputClass('pl-7')}
              />
            </div>
          </div>
        </div>

        <div>
          <FieldLabel htmlFor={remarkId}>{t('packages.form.remark')}</FieldLabel>
          <input
            id={remarkId}
            type="text"
            value={draft.remark}
            placeholder={t('packages.form.remarkPlaceholder')}
            onChange={(event) => patch({ remark: event.target.value })}
            className={inputClass()}
          />
        </div>

        {editing ? (
          // MYP-123: `package_list` never says which services a package has, so an edit has nothing
          // true to send for them — the section is replaced by a note, and the update omits them.
          <div>
            <h3 className="text-sm font-semibold text-ink">{t('packages.form.services.title')}</h3>
            <p className="mt-1.5 text-sm text-muted">{t('packages.form.services.editLocked')}</p>
          </div>
        ) : (
          <div>
            <h3 className="text-sm font-semibold text-ink">{t('packages.form.services.title')}</h3>
            {/*
            Five outcomes, each its own line: no warehouse chosen yet, the list still loading, the
            list failed (with a retry — the package list itself stays usable), a warehouse that
            offers nothing (live: most of them), and the checklist.
          */}
            {!draft.warehouseId ? (
              <p className="mt-1.5 text-sm text-muted">
                {t('packages.form.services.selectWarehouseFirst')}
              </p>
            ) : servicesStatus === 'loading' ? (
              <p className="mt-1.5 text-sm text-muted">{t('packages.form.services.loading')}</p>
            ) : servicesStatus === 'error' ? (
              <p className="mt-1.5 text-sm text-muted">
                {t('packages.form.services.loadFailed')}{' '}
                <button type="button" onClick={reloadServices} className={actionLinkClass()}>
                  {t('packages.list.retry')}
                </button>
              </p>
            ) : availableServices.length > 0 ? (
              <div className="mt-2 grid gap-3">
                {availableServices.map((service) => {
                  const checked = draft.serviceIds.includes(service.id);
                  return (
                    <ServiceCard
                      key={service.id}
                      imageUrl={service.imageUrl}
                      name={service.name}
                      nameNative={service.nameNative}
                      // The vendor's notice is staff-written and arrives with HTML entities
                      // (`&rsquo;`) — reduced to plain text, the same one place messages go through.
                      description={sanitiseMessageContent(service.description)}
                      feeCents={service.feeCents}
                      currency={service.currency}
                      priceFallback={t('packages.form.services.pricedByWarehouse')}
                      checked={checked}
                      onToggle={() => toggleService(service.id)}
                      note={
                        checked && service.requiresConfirmation
                          ? t('orders.newOrder.delivery.confirmNote')
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <p className="mt-1.5 text-sm text-muted">{t('packages.form.services.noneOffered')}</p>
            )}

            <p className="mt-2 text-xs text-muted">{t('packages.form.services.note')}</p>
          </div>
        )}
      </form>
    </Sheet>
  );
}
